import { FC, useEffect, useMemo, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import History from "@tiptap/extension-history";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import _reject from "lodash/reject";

import { clsxMerge } from "shared/lib/helpers";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";

import { EditorMenu } from "./editor-menu";
import {
  normalizeEmailEditorContent,
  processEmailEditorContent,
} from "./utils";
import { COMMON_TIPTAP_EXTENSIONS } from "./constants";
import {
  EMAIL_BODY_CHARACTER_LIMIT,
  shouldShowCharacterCount,
} from "./character-count";
import { TipTapExtensions } from "./editor-extensions";
import striptags from "striptags";

export interface EmailEditorContentI {
  plainText?: string;
  htmlText?: string;
}

export interface EmailEditorPropsI extends PropsWithClassNameI {
  shouldImmediatelyRender?: boolean;
  menuClassName?: string;
  content?: string;
  initialContent?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  /**
   * Plain-text character ceiling. See EMAIL_BODY_CHARACTER_LIMIT.
   *
   * Read once, when the editor mounts. `useEditor` is called without a deps
   * array and @tiptap/react skips re-initialisation in that case, so the
   * configured limit cannot be changed on a live editor - and a counter that
   * tracked a newer value than the one actually being enforced would be worse
   * than one that does not move.
   */
  characterLimit?: number;
  onChange?: (content: EmailEditorContentI) => void;
}

/**
 * Filter out Document and Paragraph to then replace them with custom blocks
 * to make Gmail-like formatting possible. That's needed to ensure that emails
 * are sent in the format that Gmail and other email clients expect.
 */
const TIPTAP_EXTENSIONS_FOR_EMAIL_EDITOR = [
  ..._reject(COMMON_TIPTAP_EXTENSIONS, ({ name }) =>
    ["paragraph"].includes(name)
  ),
  TipTapExtensions.DivBlock,
];

// Note: this component only has a `initialContent` prop, and no `value` prop because the live value of it is controlled interally by the Editor
export const EmailEditor: FC<EmailEditorPropsI> = ({
  shouldImmediatelyRender = true,
  className,
  menuClassName,
  content,
  initialContent,
  placeholder,
  error,
  disabled,
  characterLimit = EMAIL_BODY_CHARACTER_LIMIT,
  onChange = () => {},
}) => {
  const isInitialMount = useRef(true); // Track initial render

  /**
   * Frozen at mount, so display and enforcement can never disagree: the
   * extension's limit is baked into the editor instance and a later prop
   * change would move the counter without moving the ceiling.
   */
  const [limit] = useState(characterLimit);

  /**
   * Mirrors the extension's own count into React state, because the limit is
   * enforced silently: over it, Tiptap drops the transaction and the editor
   * simply stops responding. The number on screen is the only warning a rep
   * gets before that happens.
   */
  const [characterCount, setCharacterCount] = useState(0);

  const editorExtensions = useMemo(
    () => [
      ...TIPTAP_EXTENSIONS_FOR_EMAIL_EDITOR,
      Link.extend({
        inclusive: false,

        // Override the default setLink command to enforce https
        addCommands() {
          return {
            setLink:
              (attributes) =>
              ({ commands }) => {
                let href = attributes.href;

                if (
                  href &&
                  !href.match(/^https?:\/\//) &&
                  !href.startsWith("mailto:")
                ) {
                  // Force https:// if no protocol is provided,
                  // but only for non-mailto links
                  href = `https://${href}`;
                }

                return commands.setMark("link", { href });
              },
            unsetLink:
              () =>
              ({ commands }) => {
                return commands.unsetMark("link");
              },
          };
        },
      }).configure({
        protocols: ["https", "mailto"],
        autolink: true,
        defaultProtocol: "https",
        shouldAutoLink: (url) => {
          // Autolink if it’s a domain-like string (with or without protocol)
          const looseUrlPattern =
            /^(https?:\/\/)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/.*)?$/;

          return looseUrlPattern.test(url);
        },
        HTMLAttributes: {
          rel: null,
          target: null,
        },
      }),
      History.configure({
        depth: 10,
      }),
      Placeholder.configure({
        placeholder,
      }),
      CharacterCount.configure({
        mode: "textSize",
        limit,
      }),
    ],
    [placeholder, limit]
  );

  const editor = useEditor({
    content: normalizeEmailEditorContent(
      striptags(content || "") ? content : initialContent
    ),
    extensions: editorExtensions,
    immediatelyRender: shouldImmediatelyRender,
    parseOptions: {
      preserveWhitespace: "full",
    },
    editorProps: {
      attributes: {
        class: clsxMerge(
          "focus:outline-none typography-body-4 [&>p]:mb-4",
          "[&_a]:text-[#0F35FF] [&_a]:underline [&_a]:cursor-pointer"
        ),
      },
      // Spaces are handled by ProseMirror as normal input. Runs of spaces are
      // preserved on the wire by prepareEmailHTMLForSend at send time; see
      // shared/lib/helpers/email.
      handleKeyDown: () => false,
    },
    onUpdate: ({ editor }) => {
      // Get plain text and HTML
      const plainText = editor.getText();
      const htmlText = editor.getHTML();

      setCharacterCount(editor.storage.characterCount.characters());

      onChange(
        processEmailEditorContent({
          plainText: plainText,
          htmlText: htmlText,
        })
      );
    },
  });

  useEffect(() => {
    if (isInitialMount.current) {
      // Only set content on initial mount
      editor?.commands.setContent(
        normalizeEmailEditorContent(initialContent),
        true,
        { preserveWhitespace: "full" }
      );
      isInitialMount.current = false;
    }
    // Do not reset content on subsequent initialContent changes
  }, [initialContent, editor]);

  useEffect(() => {
    if (content) {
      if (!editor) {
        return;
      }

      const { from, to } = editor.state.selection;

      editor.commands.setContent(normalizeEmailEditorContent(content), true, {
        preserveWhitespace: "full",
      });

      /**
       * Set the cursor back to the same position
       * because the content is set to an empty string
       * and the cursor is lost or the end of the content
       */
      editor.commands.setTextSelection({ from, to });
    }
  }, [content, editor]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  /**
   * `onUpdate` does not fire for content set programmatically, so a draft
   * loaded at the ceiling would show a count of zero until the first keystroke.
   */
  useEffect(() => {
    if (editor) {
      setCharacterCount(editor.storage.characterCount.characters());
    }
  }, [editor, content, initialContent]);

  return (
    <div>
      <div className={clsxMerge("rounded-lg bg-white", className)}>
        <EditorMenu editor={editor} className={menuClassName} />

        <div className="p-4" onMouseDown={() => editor?.commands.focus()}>
          <EditorContent editor={editor} className="min-h-[60px] cursor-text" />
        </div>

        {shouldShowCharacterCount(characterCount, limit) && (
          <div
            className={clsxMerge(
              "animate-fadein typography-body-5 px-4 pb-2 text-right",
              characterCount >= limit ? "text-error-content" : "text-black/40"
            )}
          >
            {characterCount >= limit
              ? `${characterCount.toLocaleString()} / ${limit.toLocaleString()} characters - this is the maximum`
              : `${characterCount.toLocaleString()} / ${limit.toLocaleString()} characters`}
          </div>
        )}
      </div>

      {error && (
        <div className="text-error-content mt-1 flex items-center gap-1 text-xs">
          <ExclamationCircleIcon className="w-5" />
          {error}
        </div>
      )}
    </div>
  );
};
